import { useMemo } from "react";
import type { InstrumentSlot } from "../../../../../src/lib/audio";
import { useStoreSelector } from "../../../../../src/lib/store";
import {
  WT_PRESET_CATEGORIES,
  wtCategorySortIndex,
  type WtPresetCategoryId,
} from "../../lib/wavetableBank";
import { getLabActions } from "../../labActions";
import { projectStore } from "../../store/projectStore";
import { SearchableSelect, type SearchableSelectOption } from "../SearchableSelect";

/**
 * Bank / category controls for wavetable instruments — mirrors InstrumentBankBar.
 */
export function WavetableBankBar({ slot }: { slot: InstrumentSlot }) {
  const bank = useStoreSelector(projectStore, s => s.wavetableBank);
  const instruments = useStoreSelector(projectStore, s => s.instruments);
  const presetName = instruments[slot]?.kind === "wavetable"
    ? instruments[slot].preset.name
    : "";
  const selected =
    bank.find(entry => entry.name === presetName)?.id
    ?? bank[0]?.id
    ?? "";
  const entry = bank.find(item => item.id === selected);

  const bankOptions = useMemo((): SearchableSelectOption[] => {
    const ordered = bank.slice().sort((a, b) => {
      const byCat = wtCategorySortIndex(a.category) - wtCategorySortIndex(b.category);
      if (byCat !== 0) {
        return byCat;
      }
      return a.name.localeCompare(b.name);
    });
    return ordered.map(item => ({
      value: item.id,
      label: item.name,
      group: item.category,
      hint: item.tags?.join(", "),
    }));
  }, [bank]);

  const categoryOptions = useMemo(
    (): SearchableSelectOption[] =>
      WT_PRESET_CATEGORIES.map(cat => ({ value: cat.id, label: cat.label })),
    [],
  );

  const categoryFilters = useMemo(
    () => WT_PRESET_CATEGORIES.map(entry => ({ id: entry.id, label: entry.label })),
    [],
  );

  return (
    <div className="flex shrink-0 flex-col gap-[0.35rem] border-b border-white/[0.06] px-2 py-1.5">
      <SearchableSelect
        className="w-full"
        ariaLabel="Wavetable preset bank"
        searchPlaceholder="Search name, category, or tag"
        emptyMessage="No presets match."
        value={selected}
        options={bankOptions}
        filters={categoryFilters}
        onChange={bankId => getLabActions()?.applyWavetableBankEntry?.(slot, bankId)}
      />

      <div className="flex items-end gap-[0.45rem]">
        <label className="flex min-w-0 flex-1 flex-col items-stretch gap-0.5">
          <span className="text-[10px] tracking-[0.04em] text-slate-500 uppercase">Category</span>
          <SearchableSelect
            className="w-full"
            ariaLabel="Preset category"
            searchPlaceholder="Search categories"
            emptyMessage="No categories."
            value={entry?.category ?? "user"}
            options={categoryOptions}
            disabled={!entry || entry.builtin}
            onChange={value =>
              getLabActions()?.setWavetableBankCategory?.(
                selected,
                value as WtPresetCategoryId,
              )
            }
          />
        </label>
        <button
          type="button"
          className="h-8 shrink-0 self-end border border-lab-line bg-lab-panel-deep px-[0.55rem] text-[11px] disabled:cursor-not-allowed disabled:opacity-40"
          disabled={!entry || entry.builtin}
          onClick={() => getLabActions()?.editWavetableBankTags?.(selected)}
        >
          Tags…
        </button>
      </div>

      <div className="flex flex-wrap gap-1">
        <BankAction onClick={() => getLabActions()?.saveWavetableBank?.(slot)}>Save</BankAction>
        <BankAction onClick={() => getLabActions()?.saveWavetableBankAs?.(slot)}>Save as…</BankAction>
        <BankAction onClick={() => getLabActions()?.renameWavetableBank?.(selected)}>Rename</BankAction>
        <BankAction
          disabled={!entry || entry.builtin}
          onClick={() => getLabActions()?.deleteWavetableBank?.(selected)}
        >
          Delete
        </BankAction>
        <BankAction onClick={() => getLabActions()?.exportWavetableBank?.(selected)}>Export</BankAction>
        <BankAction onClick={() => getLabActions()?.exportAllWavetableBank?.()}>Export all</BankAction>
        <BankAction onClick={() => getLabActions()?.importWavetableBank?.()}>Import…</BankAction>
      </div>
    </div>
  );
}

function BankAction({
  children,
  onClick,
  disabled,
}: {
  children: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="border border-lab-line bg-lab-panel-deep px-1.5 py-[0.22rem] text-[11px] disabled:cursor-not-allowed disabled:opacity-40"
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
